import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProjectStats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  const stats = [
    { icon: 'Code2', label: 'Projects Completed', value: 150, suffix: '+', color: 'var(--color-primary)' },
    { icon: 'Users', label: 'Happy Clients', value: 85, suffix: '+', color: 'var(--color-secondary)' },
    { icon: 'Award', label: 'Awards Won', value: 12, suffix: '', color: 'var(--color-accent)' },
    { icon: 'TrendingUp', label: 'Success Rate', value: 98, suffix: '%', color: 'var(--color-success)' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef?.current) {
      observer?.observe(statsRef?.current);
    }

    return () => {
      if (statsRef?.current) {
        observer?.unobserve(statsRef?.current);
      }
    };
  }, []);

  const Counter = ({ end, duration = 2, suffix = '' }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);

        if (progress < 1) {
          setCount(Math.floor(end * progress));
          requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      requestAnimationFrame(animate);
    }, [isVisible, end, duration]);

    return (
      <span className="font-data">
        {count}{suffix}
      </span>
    );
  };

  return (
    <div ref={statsRef} className="bg-card rounded-xl p-6 md:p-8 border border-border shadow-md mb-6 md:mb-8">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2">
          Project Statistics
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Numbers that speak for themselves
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats?.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="text-center p-4 md:p-6 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
          >
            <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${stat?.color}20` }}>
              <Icon name={stat?.icon} size={24} color={stat?.color} />
            </div>
            <div className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2" style={{ color: stat?.color }}>
              <Counter end={stat?.value} suffix={stat?.suffix} />
            </div>
            <div className="text-xs md:text-sm text-muted-foreground font-medium">
              {stat?.label}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ProjectStats;